<template>
  <div :class="['documents-company-detail', { 'documents-company-detail--drawer': isDrawer, 'documents-company-detail--page': !isDrawer }]">
    <!-- Header Section: In drawer it serves as sticky header; in page mode it's a sleek card -->
    <div :class="['documents-detail__header', isDrawer ? 'px-6 pt-5 pb-4' : 'px-6 py-5 rounded-lg mb-6 elevation-1']" data-testid="documents-detail-header">
      <div class="d-flex align-start justify-space-between">
        <div class="pr-3 flex-grow-1">
          <div class="d-flex align-center flex-wrap">
            <span :class="['font-weight-bold text-truncate', isDrawer ? 'text-h6' : 'text-h5']" :style="{ maxWidth: isDrawer ? '580px' : '800px' }">
              {{ companyName }}
            </span>
            <v-chip
              v-if="payload && !payload.company.exists"
              x-small
              color="error"
              outlined
              class="ml-2"
            >
              {{ $t('documents.danglingCompanyId') }}
            </v-chip>
          </div>

          <div v-if="payload && payload.company.cnpj" class="caption grey--text mt-1">
            CNPJ: {{ formatCnpj(payload.company.cnpj) }}
          </div>
          <div v-if="payload && payload.company.company_id" class="caption grey--text text--lighten-1 mt-1 d-flex align-center">
            <span class="mr-1">Company ID:</span>
            <copyable-id
              :id="payload.company.company_id"
              type="company"
              :number-of-charts="100"
            />
          </div>
        </div>

        <!-- Drawer close button (only rendered in drawer mode) -->
        <v-btn v-if="isDrawer" icon small class="close-btn" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Tab controls -->
      <div v-if="payload && !loading" :class="isDrawer ? 'mt-5' : 'mt-6'">
        <v-tabs v-model="tab" background-color="transparent" class="detail-tabs" hide-slider>
          <v-tab class="detail-tab text-capitalize" data-testid="documents-tab-kyc">
            <v-icon small left>mdi-file-account-outline</v-icon>
            {{ $t('documents.kycTab') }}
            <v-chip
              x-small
              class="ml-2 tab-count-chip"
              :color="kycDocuments.length ? 'aliveTransp' : undefined"
              :text-color="kycDocuments.length ? 'alive' : undefined"
            >
              {{ kycDocuments.length }}
            </v-chip>
          </v-tab>

          <v-tab class="detail-tab text-capitalize" data-testid="documents-tab-operations">
            <v-icon small left>mdi-swap-horizontal</v-icon>
            {{ $t('documents.operationsTab') }}
            <v-chip
              x-small
              class="ml-2 tab-count-chip"
              :color="operationDocumentCount ? 'primaryTransp' : undefined"
              :text-color="operationDocumentCount ? 'primary' : undefined"
            >
              {{ operationDocumentCount }}
            </v-chip>
          </v-tab>
        </v-tabs>
      </div>
    </div>

    <v-divider v-if="isDrawer" class="detail-divider" />

    <!-- Content area -->
    <div :class="['documents-detail__content', isDrawer ? 'px-6 py-4 flex-grow-1' : '']">
      <div v-if="loading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="48" />
        <div class="grey--text mt-4 text-body-2">{{ $t('documents.loading') }}</div>
      </div>

      <template v-else-if="payload">
        <v-tabs-items v-model="tab" style="background: transparent;">
          <v-tab-item>
            <documents-kyc-tab
              :documents="kycDocuments"
              :is-pro="isPro"
              :downloading-sha="downloadingSha"
              :zipping="zippingKyc"
              @download="$emit('download-kyc', $event)"
              @download-zip="$emit('download-kyc-zip')"
            />
          </v-tab-item>

          <v-tab-item>
            <documents-operations-tab
              :operations="payload.operations"
              :is-pro="isPro"
              :downloading="downloadingOperation"
              :zipping="zippingOperations"
              @download="$emit('download-operation', $event)"
              @download-zip="$emit('download-operations-zip')"
            />
          </v-tab-item>
        </v-tabs-items>
      </template>

      <div v-else class="text-center grey--text py-16">
        {{ $t('documents.loadFailed') }}
      </div>
    </div>
  </div>
</template>

<script>
import CopyableId from '@/components/common/CopyableId.vue'
import DocumentsKycTab from './DocumentsKycTab.vue'
import DocumentsOperationsTab from './DocumentsOperationsTab.vue'
import convertToCNPJ from '@/utils.js'

export default {
  name: 'DocumentsCompanyDetail',

  components: {
    CopyableId,
    DocumentsKycTab,
    DocumentsOperationsTab,
  },

  props: {
    // True when rendered inside v-navigation-drawer, false when full-page
    isDrawer: { type: Boolean, default: false },
    isPro:    { type: Boolean, default: false },
    loading:  { type: Boolean, default: false },
    payload:  { type: Object, default: null },

    downloadingSha:       { type: String, default: null },
    downloadingOperation: { type: Object, default: null },
    zippingKyc:           { type: Boolean, default: false },
    zippingOperations:    { type: Boolean, default: false },
  },

  emits: [
    'close',
    'download-kyc',
    'download-operation',
    'download-kyc-zip',
    'download-operations-zip',
  ],

  data () {
    return {
      tab: 0,
    }
  },

  computed: {
    companyName () {
      if (!this.payload) return ''
      return this.payload.company.name || this.payload.company.company_id || '—'
    },
    kycDocuments () {
      return this.payload && this.payload.kyc ? this.payload.kyc.documents : []
    },
    operationDocumentCount () {
      if (!this.payload) return 0
      return (this.payload.operations || []).reduce(
        (total, op) => total + (op.documents || []).length, 0)
    },
  },

  methods: {
    formatCnpj (cnpj) {
      return convertToCNPJ(cnpj)
    },
    resetTab () {
      this.tab = 0
    },
  },
}
</script>

<style scoped lang="scss">
.documents-company-detail {
  display: flex;
  flex-direction: column;
}

.documents-company-detail--drawer {
  height: 100%;

  .documents-detail__header {
    background: #181818;
    flex-shrink: 0;
  }

  .documents-detail__content {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.documents-company-detail--page {
  width: 100%;

  .documents-detail__header {
    background: #181818;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
}

.detail-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.detail-tabs {
  max-width: 460px;

  .detail-tab {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px !important;
    color: rgba(255, 255, 255, 0.7) !important;
    font-size: 12px !important;
    font-weight: 500;
    margin-right: 8px;
    min-height: 34px;
    height: 34px;
    transition: all 0.15s ease-in-out;

    &.v-tab--active {
      background: rgba(0, 115, 255, 0.12) !important;
      border-color: rgba(0, 115, 255, 0.5) !important;
      color: #ffffff !important;
      font-weight: 600;
    }
  }
}

.tab-count-chip {
  font-size: 10px !important;
  height: 18px !important;
  font-weight: 600;
}

.close-btn {
  color: rgba(255, 255, 255, 0.6);
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }
}
</style>
